import React from 'react';
import { useParams } from 'react-router-dom';

import useGoalsMonitoring from 'containers/user/goals/goals-monitoring/monitoring-chart/hooks/use-goals-monitoring';
import CriticalElementChart from 'containers/user/goals/goals-monitoring/monitoring-chart/critical-element-chart';

import { Box } from 'ui/box';
import { Stack } from 'ui/stack';

import Button from 'components/partials/button/button';
import ResponsiveWrapper from 'components/shared/others/responsive-wrapper/responsive-wrapper';
import { Typography } from 'ui/typography';
import { Loader } from 'ui/loader';
import Breadcrumbs from 'components/partials/beadcrumbs/breadcrumbs';

import { captureInsightScreenshot } from 'helpers/utils/insight-screenshot';
import { MONITORING_CHART_MAX_WIDTH } from 'containers/user/goals/goals-monitoring/monitoring-chart/hooks/constants';

import Icons from 'assets/icons';

interface MetaDataProps {
  label: string;
  value?: React.ReactNode;
}

const renderMetaData = ({
  label,
  value = 'N/A',
}: MetaDataProps): React.JSX.Element => (
  <Stack direction="row" sx={{ alignItems: 'center', columnGap: 1 }}>
    <span className="text-sm text-gray-600">{`${label}:`}</span>
    <span className="text-sm text-gray-600 font-semibold">{value}</span>
  </Stack>
);

const AdminGoalsMonitoring = (): React.JSX.Element => {
  const {
    clientId,
    year: enteringClassYear,
    goalType,
    goalCategory,
    goalId,
  } = useParams<{
    clientId: string;
    year: string;
    goalType: string;
    goalCategory: string;
    goalId: string;
  }>();

  const { loading, monitoringSettings, displayMetrics, goalData } =
    useGoalsMonitoring(goalId, clientId);

  const breadcrumbs = [
    { label: 'Clients', href: '/admin/clients' },
    {
      label: 'Goals',
      href: `/admin/clients/${clientId}/goals/${enteringClassYear}`,
    },
    {
      label: 'Goal details',
      href: `/admin/clients/${clientId}/goals/${enteringClassYear}/${goalType}/${goalCategory}/${goalId}`,
    },
  ];

  return (
    <ResponsiveWrapper customClass="h-full">
      <Loader loading={loading} fullScreen={false} />
      <Breadcrumbs breadcrumbs={breadcrumbs} customClass="mt-7 mb-3" />

      <Typography
        data-testid="client-goal-monitoring-heading"
        variant="h2"
        component="h1"
        sx={{ mb: 1 }}
      >
        {goalData?.summary || `Goal ${goalId}`}
      </Typography>

      <Stack
        direction="row"
        sx={{ alignItems: 'center', columnGap: 2, mb: 2.5 }}
      >
        {renderMetaData({
          label: 'Cohort',
          value: monitoringSettings?.cohort,
        })}
        {renderMetaData({
          label: 'Entry term',
          value: goalData?.enteringClassYear,
        })}
      </Stack>
      <Stack
        direction="row"
        sx={{ flexWrap: 'wrap', gap: 4, alignItems: 'flex-end' }}
      >
        {!!displayMetrics.length &&
          displayMetrics.map((ce) => (
            <Box
              key={ce.criticalElementId}
              style={{ maxWidth: MONITORING_CHART_MAX_WIDTH }}
              sx={{ mb: 3 }}
            >
              <Stack
                direction="row"
                sx={{
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <Box sx={{ fontWeight: 700, color: 'tokens.gray.700' }}>
                  {ce.criticalElementSummary}
                </Box>
                <Button
                  variant="reserve-outlined"
                  size="sm"
                  className="shrink-0 ml-4"
                  iconBefore={<Icons.Insight aria-label="InsightIcon" />}
                  onClick={() =>
                    captureInsightScreenshot(
                      document.getElementById(`chart-${ce.criticalElementId}`),
                      {
                        title: '',
                        description: '',
                        summary: ce.criticalElementSummary,
                        criticalElement: ce.criticalElementSummary,
                        criticalElementId: ce.criticalElementId,
                        clientId,
                        relatedGoals: [],
                        source: 'monitoring',
                      } as never
                    )
                  }
                >
                  <span>Add insight</span>
                </Button>
              </Stack>
              <CriticalElementChart
                chartId={`chart-${ce.criticalElementId}`}
                blocks={ce.blocks}
                previousYear={
                  monitoringSettings?.comparisonYears?.[0] ?? 'Prev'
                }
                currentYear={monitoringSettings?.comparisonYears?.[1] ?? 'Curr'}
                cohortName={monitoringSettings?.cohort}
                isRestricted={ce.isRestricted}
              />
            </Box>
          ))}
      </Stack>
    </ResponsiveWrapper>
  );
};

export default AdminGoalsMonitoring;
